import { useCallback } from 'react';
import {
	type BackendPlacementMessage,
	defaultMessageBackend,
	type FrontendPlacementMessage,
	type FrontendStage,
	noValidation,
} from '@post-office/placement-common-v2';
import {
	PlacementBackend,
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/placements--statuspage/statuspage-navigation-spotlight-placement';
import { type WaitForTriggerFunction } from '@post-office/placement-contexts/hooks/use-wait-for-trigger';
import { useMountToHtmlElement } from '@post-office/components--post-office-team';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { Text } from '@atlaskit/primitives/compiled';
import Image from '@atlaskit/image';
import { FormattedMessage } from 'react-intl';

import createStatusPageSpotlightImage from '../../../assets/create-status-page-spotlight.png';
import { messages } from '../../../components/messages';
import { type MessageTemplateContext } from '../../../index';

import { useHtmlSelector } from './hooks/use-html-selector';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
} from '@post-office/placement-common-v2/create-placement/types';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';
import type { DismissEvent } from '@atlaskit/spotlight/types';

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(defaultMessageBackend);

type RenderProps = MessageTemplateContext &
	PlacementRenderProps & { waitForTrigger?: WaitForTriggerFunction };

export const STATUSPAGE_NAVIGATION_SPOTLIGHT_SELECTOR = '#statuspage-navigation-spotlight-target';

export const render: {
	type: 'render';
	validate: (input: unknown) => RenderProps;
	Component: (input: RenderProps) => JSX.Element | null;
} = PlacementFrontend.createFrontendStage({
	type: 'render',
	validate: noValidation<RenderProps>(),
	Component: ({ onDismiss, waitForTrigger }: RenderProps) => {
		const { messageDismissed } = useMessageEvent();
		const { messageInstanceClicked } = useMessageInstanceEvent();
		const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

		const spotlightTarget = useHtmlSelector({
			selector: STATUSPAGE_NAVIGATION_SPOTLIGHT_SELECTOR,
		});
		const htmlElementExists = !!spotlightTarget;

		const renderInline = useMountToHtmlElement({
			selectors: STATUSPAGE_NAVIGATION_SPOTLIGHT_SELECTOR,
			as: 'inline',
			position: 'inside',
		});

		const dismiss = useCallback(
			(e: DismissEvent) => {
				e.preventDefault();
				e.stopPropagation();
				void messageDismissed();
				void messageInstanceClicked({
					messageEventType: 'clicked',
					clickedElement: {
						clickIdentifier: 'spotlight-dismissed',
						clickedElement: 'button',
						buttonType: 'secondary',
					},
					action: { actionType: 'dismiss' },
					additionalMetadataFromFrontend: {},
				});
				fireAnalyticsEvent({
					eventType: 'ui',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'statuspageNavigationSpotlightDismiss',
				});
				onDismiss?.();
			},
			[messageDismissed, messageInstanceClicked, fireAnalyticsEvent, onDismiss],
		);

		const handlePrimaryAction = useCallback(
			(e: DismissEvent) => {
				e.preventDefault();
				e.stopPropagation();
				void messageDismissed();
				void messageInstanceClicked({
					messageEventType: 'clicked',
					clickedElement: {
						clickIdentifier: 'go-to-status-pages',
						clickedElement: 'button',
						buttonType: 'primary',
					},
					action: { actionType: 'dismiss' },
					additionalMetadataFromFrontend: { actionTaken: true },
				});
				fireAnalyticsEvent({
					eventType: 'ui',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'statuspageNavigationSpotlightGoToStatusPages',
				});
				globalThis.open(
					'https://support.atlassian.com/jira-service-management-cloud/docs/manage-and-customize-your-status-pages/',
					'_blank',
				);
				onDismiss?.();
			},
			[messageDismissed, messageInstanceClicked, fireAnalyticsEvent, onDismiss],
		);

		if (!waitForTrigger) {
			return null;
		}

		const messageContent = (
			<div>
				{renderInline(
					<PopoverProvider>
						<PopoverTarget>
							<div />
						</PopoverTarget>
						<PopoverContent dismiss={dismiss} placement="right-end" isVisible={true}>
							<SpotlightCard>
								<SpotlightHeader>
									<SpotlightHeadline>
										<FormattedMessage {...messages.statusPageSpotlightNavigationHeading} />
									</SpotlightHeadline>
									<SpotlightControls>
										<SpotlightDismissControl autoFocus={false} onClick={dismiss} />
									</SpotlightControls>
								</SpotlightHeader>
								<SpotlightMedia>
									<Image
										src={createStatusPageSpotlightImage}
										alt="Create and manage status pages"
									/>
								</SpotlightMedia>
								<SpotlightBody>
									<Text>
										<FormattedMessage {...messages.statusPageSpotlightNavigationBody} />
									</Text>
								</SpotlightBody>
								<SpotlightFooter>
									<SpotlightActions>
										<SpotlightPrimaryAction onClick={handlePrimaryAction}>
											<FormattedMessage {...messages.statusPageSpotlightNavigationAction} />
										</SpotlightPrimaryAction>
									</SpotlightActions>
								</SpotlightFooter>
							</SpotlightCard>
						</PopoverContent>
					</PopoverProvider>,
				)}
			</div>
		);

		return waitForTrigger({ isTriggerActive: htmlElementExists, messageContent });
	},
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
